import { Box, useTheme, Button, Paper, Typography } from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { useRoutePersistence } from '@hooks/useRoutePersistence';
import { clearRouteHistory } from '@services/routePersistenceService';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import LoanProgress from './LoanProgress';
import BudgetGridEditView from './BudgetGridEditView';
import { BudgetEditItem } from '@models/drawRequests/budgetEditItem';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDrawRequestCommandApi } from '@hooks/api/useDrawRequestCommandApi';
import { DrawRequestCreateRequestModelNew } from '@models/drawRequests/drawRequestCreateRequestModelNew';
import { DrawRequestDetail } from '@models/drawRequests/drawRequestDetail';
import FileSelector from '@components/sharedComponents/FileSelector';
import { useAttachmentApi, fileToBase64 } from '@hooks/api/useAttachmentApi';
import { DRAW_MANAGER_TAB_REC_ID } from '@constants/drawManagerConstants';
import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILE_COUNT,
  MAX_FILE_SIZE_MB,
} from '@constants/fileTypeConstants';
import { AttOwnerType } from '@enums/attOwnerTypes';
import { DocumentTypes } from '@enums/documentTypes';
import { DrawManagerAttachmentRequestModel } from '@models/drawRequests/drawManagerAttachmentRequestModel';
import { toast } from 'react-hot-toast';
import { useApp } from '@context/AppContext';
import CreateDrawMessage from './CreateDrawMessage';
import { useDrawRequestMessageApi } from '@hooks/api/useDrawRequestMessageApi';
import { DrawRequestMessageModel } from '@models/drawRequests/DrawRequestMessageModel';
import { showCustomErrorToast } from '@utils/customToast';
import { colors } from '@theme/colors';
import { useDrawRequestMessages } from '@context/DrawRequestMessageContext';

const fileStorage = new Map<string, File[]>();

export default function RequestDraw() {
  const theme = useTheme();
  const { isTabletScreen, isSmallDesktop, companyId } = useApp();
  const isMobile = isTabletScreen;
  const posthog = usePostHog();
  const isTablet = isTabletScreen && !isSmallDesktop;
  const navigate = useNavigate();
  const location = useLocation();
  const { account, sourceRecId } = useParams<{
    account: string;
    sourceRecId: string;
  }>();
  const { setIntentionalPath } = useRoutePersistence();
  const [budgetItems, setBudgetItems] = useState<BudgetEditItem[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const {
    pendingMessages,
    addMessage,
    removeMessage,
    clearMessages,
    setPendingMessages,
  } = useDrawRequestMessages();
  const [drawRequestRecId, setDrawRequestRecId] = useState<string>('');
  const submitDrawRequestMutation =
    useDrawRequestCommandApi.useSubmitDrawRequest();
  const addAttachmentMutation = useAttachmentApi.useAddAttachment();
  const bulkSaveMessagesMutation =
    useDrawRequestMessageApi.useSaveBulkMessages();

  const { data: attachmentsData } =
    useAttachmentApi.useGetAttachmentsByDrawRequest(
      drawRequestRecId,
      sourceRecId || '',
      !!drawRequestRecId && !!sourceRecId
    );

  const storageKey =
    account && sourceRecId ? `${account}-${sourceRecId}` : null;
  const hasRestoredFilesRef = useRef(false);

  const isFreshEntry =
    !location.state?.budgetItems && !location.state?.pendingMessages;

  useEffect(() => {
    if (isFreshEntry && storageKey) {
      fileStorage.delete(storageKey);
      hasRestoredFilesRef.current = false;
      setBudgetItems([]);
      setPendingMessages([]);
      setSelectedFiles([]);
    }
  }, [isFreshEntry, storageKey]);

  useEffect(() => {
    if (!isFreshEntry) {
      if (location.state?.budgetItems) {
        setBudgetItems(location.state.budgetItems);
      }
      if (location.state?.pendingMessages) {
        setPendingMessages(location.state.pendingMessages);
      }
      if (
        storageKey &&
        fileStorage.has(storageKey) &&
        !hasRestoredFilesRef.current &&
        selectedFiles.length === 0
      ) {
        const storedFiles = fileStorage.get(storageKey)!;
        setSelectedFiles(storedFiles);
        hasRestoredFilesRef.current = true;
      }
    }
  }, [
    isFreshEntry,
    location.state?.budgetItems,
    location.state?.pendingMessages,
    setPendingMessages,
    storageKey,
    selectedFiles.length,
  ]);

  const handleBudgetItemsChange = (items: BudgetEditItem[]) => {
    setBudgetItems(items);
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleFilesChange = (files: File[]) => {
    setSelectedFiles(files);
    if (storageKey) {
      fileStorage.set(storageKey, files);
    }
  };

  useEffect(() => {
    if (attachmentsData && attachmentsData.length > 0 && drawRequestRecId) {
      const existingFiles: File[] = attachmentsData.map(attachment => {
        const file = new File([''], attachment.fileName || 'Unknown', {
          type: 'application/octet-stream',
        });

        (file as any).attachmentId = attachment.recId;
        (file as any).isExistingAttachment = true;
        (file as any).description =
          attachment.description || attachment.fileName;
        return file;
      });

      const existingAttachmentIds = new Set(
        existingFiles.map(f => (f as any).attachmentId)
      );
      setSelectedFiles(currentFiles => {
        const newFiles = currentFiles.filter(
          file =>
            !(file as any).isExistingAttachment &&
            !existingAttachmentIds.has((file as any).attachmentId)
        );
        const mergedFiles = [...existingFiles, ...newFiles];
        if (storageKey) {
          fileStorage.set(storageKey, mergedFiles);
        }
        return mergedFiles;
      });
    }
  }, [attachmentsData, drawRequestRecId, storageKey]);

  const handleMessageAdd = (message: DrawRequestMessageModel) => {
    addMessage(message);
  };

  const handleMessageRemove = (index: number) => {
    removeMessage(index);
  };

  const drawTotal = budgetItems.reduce(
    (total, item) => total + (item.requestAmount || 0),
    0
  );

  const handleUploadFilesWithDrawRequestId = async (
    drawRequestId: string,
    drawRequestRecId: string
  ) => {
    if (!sourceRecId || selectedFiles.length === 0) return;

    try {
      const updatedFiles = selectedFiles.map(file => {
        (file as any).description = `${drawRequestId}_${file.name}`;
        return file;
      });
      setSelectedFiles(updatedFiles);

      for (const file of selectedFiles) {
        const fileContent = await fileToBase64(file);

        const attachmentRequest: DrawManagerAttachmentRequestModel = {
          OwnerRecId: sourceRecId,
          OwnerType: AttOwnerType.Borrower,
          DrawRequestRecId: drawRequestRecId,
          FileName: file.name,
          Document: fileContent,
          DocType: DocumentTypes.Other,
          Description: `${drawRequestId}_${file.name}`,
          TabRecId: DRAW_MANAGER_TAB_REC_ID,
          SourceRecId: sourceRecId,
        };

        await addAttachmentMutation.mutateAsync(attachmentRequest);
      }

      setSelectedFiles([]);
    } catch (error) {
      toast.error(error as string);
    }
  };

  const navigateToMyDraws = () => {
    const fromPath = location.state?.from;
    const expandedLoan = location.state?.expandedLoan;

    if (
      fromPath &&
      fromPath.includes(PrivateRoutes.LOAN_DETAILS.split(':')[0])
    ) {
      navigate(fromPath, {
        state: {
          expandedLoan: expandedLoan,
          scrollTo: 'my-draws-section',
        },
      });
    } else if (account && sourceRecId) {
      navigate(
        PrivateRoutes.LOAN_DETAILS.replace(':account', account).replace(
          ':sourceRecId',
          sourceRecId
        ),
        {
          state: {
            expandedLoan: sourceRecId,
            scrollTo: 'my-draws-section',
          },
        }
      );
    } else {
      clearRouteHistory();
      setIntentionalPath(PrivateRoutes.ROOT);
      navigate(PrivateRoutes.ROOT, {
        replace: true,
        state: {
          scrollTo: 'your-loans-tile',
          expandedLoan: sourceRecId || expandedLoan,
        },
      });
    }
  };

  const handleSubmitRequest = async () => {
    if (!isFormValid || !sourceRecId) return;

    try {
      const drawRequestDetails: DrawRequestDetail[] = budgetItems
        .filter(item => item.budgetItem && item.requestAmount > 0)
        .map(item => ({
          recId: '',
          budgetItemRecId: item.budgetItem,
          remainingBudget: item.remainingAmount,
          drawAmount: item.requestAmount,
          drawRequestRecId: '',
          pctCompleted: item.percentComplete || 0,
        }));

      const request: DrawRequestCreateRequestModelNew = {
        drawRequestDetails,
        sourceRecId,
      };

      const drawRequestResponse =
        await submitDrawRequestMutation.mutateAsync(request);

      if (drawRequestResponse?.drawRequestRecId) {
        setDrawRequestRecId(drawRequestResponse.drawRequestRecId);
      }

      if (selectedFiles.length > 0) {
        await handleUploadFilesWithDrawRequestId(
          drawRequestResponse?.drawRequestId || '',
          drawRequestResponse?.drawRequestRecId || ''
        );
      }

      if (pendingMessages.length > 0 && account && sourceRecId) {
        try {
          await bulkSaveMessagesMutation.mutateAsync({
            drawRequestId: drawRequestResponse?.drawRequestRecId,
            messages: pendingMessages,
            companyId: companyId,
            loanAccount: account,
            sourceRecId: sourceRecId,
          });
          clearMessages();
        } catch (error) {
          console.error('Error saving messages:', error);
          toast.error('Draw request submitted but failed to save messages');
        }
      } else {
        clearMessages();
      }

      if (storageKey) {
        fileStorage.delete(storageKey);
      }

      posthog?.capture('draw_request_submitted', {
        draw_request_id: drawRequestResponse?.drawRequestId,
        source_rec_id: sourceRecId,
        loan_account: account,
        draw_total: drawTotal,
        budget_item_count: drawRequestDetails.length,
        file_count: selectedFiles.length,
        message_count: pendingMessages.length,
        company_id: companyId,
      });

      navigateToMyDraws();
    } catch (error) {
      toast.error(error as string);
      posthog?.captureException(error as Error, {
        extra: { source_rec_id: sourceRecId, loan_account: account },
      });
    }
  };

  const renderLoanProgressPaper = () => (
    <Paper
      component="section"
      elevation={0}
      aria-labelledby="loan-progress-heading"
      sx={{
        p: '22px 18px',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
      }}
    >
      <Typography
        id="loan-progress-heading"
        variant="h2"
        component="h2"
        sx={{
          fontSize: '18px',
          fontWeight: 600,
          mb: 2,
          color: theme.palette.text.primary,
        }}
      >
        Loan Progress
      </Typography>
      <LoanProgress sourceRecId={sourceRecId || ''} budgetItems={budgetItems} />
    </Paper>
  );

  const renderBudgetEditPaper = () => (
    <Paper
      component="section"
      elevation={0}
      aria-labelledby="budget-edit-heading"
      sx={{
        p: '22px 18px',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        mb: isMobile ? 3 : 0,
      }}
    >
      <Typography
        id="budget-edit-heading"
        variant="h2"
        component="h2"
        sx={{
          fontSize: '18px',
          fontWeight: 600,
          mb: 2,
          color: theme.palette.text.primary,
        }}
      >
        Budget Items
      </Typography>
      <BudgetGridEditView
        sourceRecId={sourceRecId || ''}
        initialItems={budgetItems}
        onItemsChange={handleBudgetItemsChange}
        onValidationChange={handleValidationChange}
        buttonText="Submit Request"
        submittingText="Submitting..."
      />
    </Paper>
  );

  const renderMobileLayout = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {renderLoanProgressPaper()}
      {renderBudgetEditPaper()}
    </Box>
  );

  const renderSubmitButton = () => (
    <Button
      variant="contained"
      onClick={isFormValid ? handleSubmitRequest : undefined}
      disabled={!isFormValid || submitDrawRequestMutation.isPending}
      aria-label={
        submitDrawRequestMutation.isPending
          ? 'Submitting draw request'
          : 'Submit draw request'
      }
      sx={{
        px: { xs: 2, md: 4 },
        py: 2,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        flexShrink: 0,
        ...(isMobile && {
          flex: 1,
          minWidth: 0,
        }),
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      {submitDrawRequestMutation.isPending ? 'Submitting...' : 'Submit Request'}
    </Button>
  );

  const handleAttachmentClick = async (file: File) => {
    const attachmentId = (file as any).attachmentId;
    const isExistingAttachment = (file as any).isExistingAttachment;
    const fileName = (file as any).description || file.name;

    if (isExistingAttachment && attachmentId && sourceRecId) {
      try {
        await useAttachmentApi.downloadAttachment(
          attachmentId,
          sourceRecId,
          fileName
        );
      } catch (error: any) {
        showCustomErrorToast({
          message: error?.message || 'Failed to download attachment',
        });
      }
    } else {
      try {
        const url = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        showCustomErrorToast({
          message: error?.message || 'Failed to download file',
        });
      }
    }
  };

  const renderFileSelector = () => (
    <FileSelector
      selectedFiles={selectedFiles}
      onFilesChange={handleFilesChange}
      maxFiles={MAX_FILE_COUNT}
      maxFileSize={MAX_FILE_SIZE_MB}
      acceptedFileTypes={ACCEPTED_FILE_EXTENSIONS}
      buttonText="View/Attach Files"
      showChips={true}
      maxChipsVisible={0}
      disableSpacer={true}
      iconOnly={isMobile}
      onFileClick={handleAttachmentClick}
      sourceRecId={sourceRecId}
    />
  );

  const handleMessagesClick = () => {
    navigate(
      `${PrivateRoutes.CREATE_DRAW_MESSAGE}?sourceRecId=${sourceRecId}&account=${account}`,
      {
        state: {
          from: location.pathname,
          expandedLoan: sourceRecId,
          budgetItems: budgetItems,
          pendingMessages: pendingMessages,
        },
      }
    );
  };

  const renderMessagesButton = () => (
    <Button
      variant="outlined"
      aria-label="View messages"
      onClick={handleMessagesClick}
      sx={{
        position: 'relative',
        minWidth: 48,
        width: 48,
        height: 48,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
        p: 0,
        '&:hover': {
          backgroundColor: theme.palette.primary.light + '20',
          borderColor: theme.palette.primary.dark,
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <MailOutline sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
    </Button>
  );

  const renderMobileFooter = () => {
    const hasExceedingAmounts = budgetItems.some(
      item => (item.requestAmount || 0) > (item.remainingAmount || 0)
    );

    return (
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: '12px 16px', md: 4.4 },
          borderRadius: '16px 16px 0 0',
          backgroundColor: 'white',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 1000,
          boxShadow: `0 -4px 20px ${colors.ui.shadow.medium}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Epilogue',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '1.4',
              color: theme.palette.text.primary,
            }}
            component="div"
            role="text"
            aria-label={`Draw total: $${drawTotal.toLocaleString()}`}
          >
            Draw Total = ${drawTotal.toLocaleString()}
          </Typography>
        </Box>

        {hasExceedingAmounts && (
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}
            role="alert"
            aria-live="polite"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <InfoIcon
                sx={{ fill: colors.orange[500], fontSize: '16px' }}
                aria-hidden="true"
              />
              <Typography
                variant="body3"
                sx={{
                  fontStyle: 'italic',
                  fontSize: '12px',
                }}
              >
                Alert: Budget item(s) exceed the allocated amount.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Third row: Three buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {renderSubmitButton()}
          {renderFileSelector()}
          {renderMessagesButton()}
        </Box>
      </Paper>
    );
  };

  const renderDesktopHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          width: '25%',
        }}
      >
        Loan Progress
      </Typography>
      <Box sx={{ width: '75%' }}>
        <FileSelector
          selectedFiles={selectedFiles}
          onFilesChange={handleFilesChange}
          maxFiles={MAX_FILE_COUNT}
          maxFileSize={MAX_FILE_SIZE_MB}
          acceptedFileTypes={ACCEPTED_FILE_EXTENSIONS}
          buttonText="Attach Files"
          showChips={true}
          maxChipsVisible={2}
          onFileClick={handleAttachmentClick}
          sourceRecId={sourceRecId}
        />
      </Box>
    </Box>
  );

  const renderDesktopContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallDesktop ? 'column' : 'row',
        gap: 3,
        alignItems: isSmallDesktop ? 'stretch' : 'flex-start',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <BudgetGridEditView
          sourceRecId={sourceRecId || ''}
          initialItems={budgetItems}
          onItemsChange={handleBudgetItemsChange}
          onValidationChange={handleValidationChange}
          showSubmitButton={!isMobile}
          buttonText="Submit Request"
          submittingText="Submitting..."
          isFormValid={isFormValid}
          onSubmitRequest={handleSubmitRequest}
          isSubmitting={submitDrawRequestMutation.isPending}
        />
      </Box>
      <Box sx={{ width: isSmallDesktop ? '100%' : 400 }}>
        <CreateDrawMessage
          onMessageAdd={handleMessageAdd}
          pendingMessages={pendingMessages}
          onMessageRemove={handleMessageRemove}
        />
      </Box>
    </Box>
  );

  const renderDesktopTabletLayout = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: '22px 18px', md: 4.4 },
        backgroundColor: 'white',
        width: '100%',
        mb: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {renderDesktopHeader()}
        <LoanProgress
          sourceRecId={sourceRecId || ''}
          budgetItems={budgetItems}
        />
        {renderDesktopContent()}
      </Box>
    </Paper>
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    const scrollToTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    };

    scrollToTop();
    const timeoutId = setTimeout(scrollToTop, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const handleBackClick = useCallback(() => {
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);

    const fromPath = location.state?.from;
    const expandedLoan = location.state?.expandedLoan;

    const isValidFromPath =
      fromPath &&
      fromPath !== location.pathname &&
      !fromPath.includes(PrivateRoutes.REQUEST_DRAW.split(':')[0]);

    if (isValidFromPath && fromPath !== PrivateRoutes.ROOT) {
      if (fromPath.includes(PrivateRoutes.LOAN_DETAILS.split(':')[0])) {
        navigate(fromPath, {
          state: {
            expandedLoan: expandedLoan,
            scrollTo: 'my-draws-section',
          },
        });
      } else {
        navigate(fromPath, {
          state: {
            expandedLoan: expandedLoan,
          },
        });
      }
    } else {
      clearRouteHistory();
      setIntentionalPath(PrivateRoutes.ROOT);
      navigate(PrivateRoutes.ROOT, {
        replace: true,
        state: {
          scrollTo: 'your-loans-tile',
          expandedLoan: sourceRecId || expandedLoan,
        },
      });
    }

    setTimeout(() => {
      setIsNavigating(false);
    }, 1000);
  }, [
    isNavigating,
    location.state,
    location.pathname,
    navigate,
    account,
    setIntentionalPath,
  ]);

  const renderBackButton = () => (
    <Box sx={{ mt: 8, mb: 3 }}>
      <Button
        onClick={handleBackClick}
        className="back-button"
        aria-label="Go back to previous page"
        sx={{
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        {'< Back'}
      </Button>
    </Box>
  );

  const renderPageTitle = () => (
    <Typography variant="pageTitle" component="h1" sx={{ mb: 4 }}>
      Request a Draw
    </Typography>
  );

  const renderMainContent = () => (
    <Box
      component="main"
      role="main"
      aria-label="Request a draw page"
      sx={{
        mx: 'auto',
        backgroundColor: 'teal.200',
        minHeight: '100vh',
        pb: isMobile || isTablet ? '220px' : 4,
        p: 2,
        pl: { xs: 2, sm: 2, md: 4 },
        pr: { xs: 2, sm: 2, md: 4 },
      }}
    >
      {renderBackButton()}
      {renderPageTitle()}
      {!isMobile ? renderDesktopTabletLayout() : renderMobileLayout()}
      {isMobile && renderMobileFooter()}
    </Box>
  );

  return renderMainContent();
}
